import React, { useState, useEffect } from 'react';
import {
  Upload,
  CheckCircle,
  RefreshCw,
  Play,
  Target,
  FileSpreadsheet,
  Package,
  ClipboardList,
  BarChart3,
  ShoppingCart,
  Warehouse
} from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getStorage, ref, uploadBytesResumable, uploadString } from 'firebase/storage';
import { getFirestore, doc, setDoc } from 'firebase/firestore';

export const TARGET_FILE_TYPES = [
  { key: 'header', label: 'Commitment Header', table: 'target_commit_header', icon: ClipboardList, color: 'red' },
  { key: 'item', label: 'Commit Item Details', table: 'target_commit_item_details', icon: Package, color: 'red' },
  { key: 'assortment', label: 'Commit Assortment Item Details', table: 'target_commit_assortment', icon: FileSpreadsheet, color: 'rose' },
  { key: 'otb', label: 'Commit OTB Details', table: 'target_commit_otb', icon: BarChart3, color: 'rose' },
  { key: 'po', label: 'Commit PO Details', table: 'target_commit_po', icon: ShoppingCart, color: 'rose' },
  { key: 'item_status', label: 'Item Status Overview', table: 'target_item_status', icon: ClipboardList, color: 'red', accept: '.xlsx,.xls,.csv' },
  { key: 'open_wip', label: 'Open WIP', table: 'target_open_wip', icon: RefreshCw, color: 'rose', accept: '.xlsx,.xls' },
  { key: 'on_hand', label: 'On Hand', table: 'target_on_hand', icon: Warehouse, color: 'red' },
  { key: 'matson_carton_dims', label: 'Matson Carton Dims', table: 'matson_carton_dims', icon: FileSpreadsheet, color: 'rose', accept: '.xlsx,.xls,.csv', selectLabel: 'Select File', writeMode: 'append' },
];

export function requireTargetTenantId(tenantId) {
  const normalized = String(tenantId || '').trim();
  if (!normalized) {
    throw new Error('Target upload tenantId is required');
  }
  return normalized;
}

export function buildTargetStoragePath(fileType, fileName, tenantId, timestamp = Date.now()) {
  const activeTenantId = requireTargetTenantId(tenantId);
  const safeName = String(fileName || `${fileType.table}.csv`).replace(/[^A-Za-z0-9._-]/g, '_');
  return `tenants/${activeTenantId}/bi-uploads/target/${fileType.table}/${timestamp}_${safeName}`;
}

export function buildTargetUploadPayload(fileType, storagePath, tenantId) {
  const activeTenantId = requireTargetTenantId(tenantId);
  return {
    bucketName: 'actualizer2.firebasestorage.app',
    filePath: storagePath,
    tableName: fileType.table,
    datasetName: fileType.datasetName || `${activeTenantId}_raw`,
    tenantId: activeTenantId,
    ...(fileType.writeMode ? { writeMode: fileType.writeMode } : {})
  };
}

export function targetUploadMetadataPath(fileType, tenantId) {
  return ['tenants', requireTargetTenantId(tenantId), 'target_upload_metadata', fileType.table];
}

export function cleanTargetCsv(text) {
  const lines = text.split(/\r?\n/);
  const dataLines = [];
  const metaLines = [];
  let hitEmpty = false;

  for (const line of lines) {
    if (!hitEmpty) {
      if (line.trim() === '') {
        hitEmpty = true;
      } else {
        dataLines.push(line);
      }
    } else {
      if (line.trim()) metaLines.push(line.trim());
    }
  }

  const metadata = {};
  for (const ml of metaLines) {
    const idx = ml.indexOf(':');
    if (idx > 0) {
      const key = ml.slice(0, idx).trim();
      const val = ml.slice(idx + 1).trim();
      metadata[key] = val;
    }
  }

  return { cleanedCsv: dataLines.join('\n'), metadata };
}

export default function TargetBalanceSheetWorkflow({ tenantId } = {}) {
  const [uploadMode, setUploadMode] = useState('system');
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncingFile, setSyncingFile] = useState(null);
  const [syncProgress, setSyncProgress] = useState({});

  const TARGET_KEYS = ['sp26_wip', 'ra_qoh_sp26'];

  const loadSyncStatus = async () => {
    try {
      const functions = getFunctions();
      const result = await httpsCallable(functions, 'getAzureSyncStatus')();
      setSyncStatus(result.data?.files?.filter(f => f.workflow === 'target') || []);
    } catch (e) { console.warn('Could not load sync status:', e.message); }
  };

  useEffect(() => { loadSyncStatus(); }, []);

  const triggerSync = async (force = false) => {
    setSyncing(true);
    const progress = {};
    TARGET_KEYS.forEach(k => { progress[k] = 'pending'; });
    setSyncProgress(progress);

    const functions = getFunctions();
    const syncFn = httpsCallable(functions, 'syncAzureData', { timeout: 570000 });

    for (const key of TARGET_KEYS) {
      setSyncingFile(key);
      setSyncProgress(prev => ({ ...prev, [key]: 'syncing' }));
      try {
        const result = await syncFn({ files: [key], force });
        const fileResult = result.data?.results?.[0];
        setSyncProgress(prev => ({ ...prev, [key]: fileResult?.status || 'success' }));
      } catch (e) {
        console.error(`Sync failed for ${key}:`, e.message);
        setSyncProgress(prev => ({ ...prev, [key]: 'error' }));
      }
    }

    setSyncingFile(null);
    setSyncing(false);
    await loadSyncStatus();
  };

  const syncSingleFile = async (key) => {
    setSyncing(true);
    setSyncingFile(key);
    setSyncProgress({ [key]: 'syncing' });
    try {
      const functions = getFunctions();
      const result = await httpsCallable(functions, 'syncAzureData', { timeout: 570000 })({ files: [key], force: true });
      const fileResult = result.data?.results?.[0];
      setSyncProgress({ [key]: fileResult?.status || 'success' });
    } catch (e) {
      console.error(`Sync failed for ${key}:`, e.message);
      setSyncProgress({ [key]: 'error' });
    }
    setSyncingFile(null);
    setSyncing(false);
    await loadSyncStatus();
  };

  const [uploads, setUploads] = useState({});

  const updateUpload = (key, patch) => {
    setUploads(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  };

  const handleFileSelect = (key, e) => {
    const file = e.target.files?.[0];
    if (file) {
      updateUpload(key, { file, result: null, error: null });
    }
  };

  const uploadFile = async (fileType) => {
    const state = uploads[fileType.key];
    if (!state?.file) return;

    updateUpload(fileType.key, { uploading: true, progress: 0, error: null });

    try {
      const activeTenantId = requireTargetTenantId(tenantId);
      const storage = getStorage();
      const isCSV = state.file.name.toLowerCase().endsWith('.csv');
      const storagePath = buildTargetStoragePath(fileType, state.file.name, activeTenantId);
      const storageRef = ref(storage, storagePath);

      let metadata = {};

      if (isCSV) {
        // CSV: read as text, strip trailing metadata, upload cleaned
        updateUpload(fileType.key, { progress: 10 });
        const rawText = await state.file.text();
        const cleaned = cleanTargetCsv(rawText);
        metadata = cleaned.metadata;

        updateUpload(fileType.key, { progress: 20 });
        await uploadString(storageRef, cleaned.cleanedCsv, 'raw', { contentType: 'text/csv' });
      } else {
        // XLSX: upload raw binary
        updateUpload(fileType.key, { progress: 10 });
        await new Promise((resolve, reject) => {
          const uploadTask = uploadBytesResumable(storageRef, state.file);
          uploadTask.on('state_changed',
            (snapshot) => {
              const progress = 10 + Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 40);
              updateUpload(fileType.key, { progress });
            },
            (error) => reject(error),
            () => resolve()
          );
        });
      }

      updateUpload(fileType.key, { progress: 55 });

      // Save metadata to Firestore (CSV only)
      if (Object.keys(metadata).length > 0) {
        const db = getFirestore();
        await setDoc(doc(db, ...targetUploadMetadataPath(fileType, activeTenantId)), {
          ...metadata,
          tenantId: activeTenantId,
          tableName: fileType.table,
          originalFilename: state.file.name,
          uploadedAt: new Date().toISOString()
        });
      }

      const functions = getFunctions();
      const processBIUpload = httpsCallable(functions, 'processBIUpload', { timeout: 600000 });

      updateUpload(fileType.key, { progress: 60 });
      const result = await processBIUpload(buildTargetUploadPayload(fileType, storagePath, activeTenantId));

      updateUpload(fileType.key, {
        progress: 100,
        uploading: false,
        result: {
          success: true,
          filename: state.file.name,
          rowCount: result.data?.rowsInserted || result.data?.rowCount || 0,
          metadata
        }
      });
    } catch (err) {
      updateUpload(fileType.key, { error: err.message, uploading: false });
    }
  };

  const resetAll = () => setUploads({});

  const completedCount = TARGET_FILE_TYPES.filter(ft => uploads[ft.key]?.result).length;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-50 to-rose-50 rounded-lg border border-red-200 p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-red-100 rounded-xl">
              <Target className="h-7 w-7 text-red-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Target Balance Sheet</h1>
              <p className="text-gray-600 text-sm">Upload Target commitment files and supporting dimension feeds</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">{completedCount}/{TARGET_FILE_TYPES.length} uploaded</span>
            <button
              onClick={resetAll}
              className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg text-sm"
            >
              <RefreshCw className="h-4 w-4" />
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Upload Mode Toggle */}
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-slate-600">Upload Mode:</span>
        <div className="flex bg-slate-100 rounded-full p-0.5">
          <button onClick={() => setUploadMode('system')}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${uploadMode === 'system' ? 'bg-red-600 text-white shadow' : 'text-slate-500 hover:text-slate-700'}`}>
            System (Azure)
          </button>
          <button onClick={() => setUploadMode('manual')}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${uploadMode === 'manual' ? 'bg-red-600 text-white shadow' : 'text-slate-500 hover:text-slate-700'}`}>
            Manual Upload
          </button>
        </div>
        {uploadMode === 'system' && (
          <button onClick={() => triggerSync(true)} disabled={syncing}
            className="ml-auto flex items-center gap-2 px-3 py-1.5 bg-white border border-red-300 rounded-lg hover:bg-red-50 text-sm text-red-700 disabled:opacity-50">
            <RefreshCw className={`h-3.5 w-3.5 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Force Refresh'}
          </button>
        )}
      </div>

      {/* System Sync Status */}
      {uploadMode === 'system' && (
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-slate-700">Azure Data Lake Sync</h3>
            {syncing && (
              <span className="text-xs text-red-600 flex items-center gap-1.5">
                <RefreshCw className="h-3 w-3 animate-spin" />
                Syncing {syncingFile || ''}...
              </span>
            )}
          </div>

          {syncing && (
            <div className="mb-4">
              <div className="flex gap-1">
                {TARGET_KEYS.map(k => {
                  const s = syncProgress[k];
                  const bg = s === 'success' ? 'bg-green-500' : s === 'skipped' ? 'bg-blue-400' : s === 'syncing' ? 'bg-red-500 animate-pulse' : s === 'error' ? 'bg-red-700' : 'bg-slate-200';
                  return <div key={k} className={`h-2 flex-1 rounded-full ${bg} transition-all duration-300`} title={k} />;
                })}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-2">
            {(syncStatus || TARGET_KEYS.map(k => ({ key: k, label: k, step: '', status: 'never_synced' }))).map(f => {
              const liveStatus = syncProgress[f.key];
              const statusColor = liveStatus === 'syncing' ? 'border-red-300 bg-red-50' :
                liveStatus === 'success' ? 'border-green-300 bg-green-50' :
                liveStatus === 'error' ? 'border-red-300 bg-red-50' :
                'border-slate-100 bg-slate-50';
              return (
                <div key={f.key} className={`flex items-center justify-between px-3 py-2.5 rounded-lg border ${statusColor} transition-all`}>
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] font-bold text-red-600 bg-red-100 px-1.5 py-0.5 rounded min-w-[40px] text-center">{f.step}</span>
                    <div>
                      <span className="text-sm font-medium text-slate-700">{f.label}</span>
                      <span className="text-[10px] text-slate-400 ml-2">{f.tableName}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {f.rowCount > 0 && <span className="text-xs font-medium text-slate-600">{Number(f.rowCount).toLocaleString()} rows</span>}
                    {f.azureLastModified && (
                      <span className="text-[10px] text-slate-400" title="Azure file timestamp">
                        File: {new Date(f.azureLastModified).toLocaleString()}
                      </span>
                    )}
                    {f.lastSync ? (
                      <span className="text-xs text-green-600" title="Last synced">
                        Synced: {new Date(f.lastSync).toLocaleString()}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">Never synced</span>
                    )}
                    {liveStatus === 'syncing' ? (
                      <RefreshCw className="h-3.5 w-3.5 text-red-500 animate-spin" />
                    ) : liveStatus === 'success' ? (
                      <CheckCircle className="h-3.5 w-3.5 text-green-500" />
                    ) : liveStatus === 'skipped' ? (
                      <span className="text-[10px] text-blue-500 font-medium">unchanged</span>
                    ) : (
                      <span className={`w-2.5 h-2.5 rounded-full ${f.status === 'success' ? 'bg-green-500' : f.status === 'error' ? 'bg-red-500' : 'bg-slate-300'}`} />
                    )}
                    <button onClick={(e) => { e.stopPropagation(); syncSingleFile(f.key); }} disabled={syncing}
                      className="px-2 py-0.5 text-[10px] font-medium text-red-600 bg-red-50 border border-red-200 rounded hover:bg-red-100 disabled:opacity-30"
                      title={`Refresh ${f.label}`}>
                      Sync
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-slate-400 mt-3">Auto-syncs Mon-Fri 8:10am, 9:10am, 10:10am, 12:10pm, 2:10pm ET</p>
        </div>
      )}

      {/* Upload Cards Grid */}
      {uploadMode === 'manual' && <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {TARGET_FILE_TYPES.map((ft, idx) => {
          const state = uploads[ft.key] || {};
          const Icon = ft.icon;
          const isCompleted = !!state.result;
          const borderClass = isCompleted
            ? 'border-green-300 bg-green-50'
            : 'border-gray-200 hover:border-red-200';

          return (
            <div key={ft.key} className={`bg-white rounded-lg border-2 ${borderClass} p-4`}>
              <div className="flex items-center gap-2 mb-3">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                  isCompleted ? 'bg-green-500 text-white' : 'bg-red-100 text-red-600'
                }`}>
                  {isCompleted ? <CheckCircle className="w-3.5 h-3.5" /> : idx + 1}
                </div>
                <Icon className={`h-5 w-5 ${isCompleted ? 'text-green-600' : 'text-red-500'}`} />
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-semibold text-gray-800 block truncate">{ft.label}</span>
                  <span className="text-[10px] text-gray-400 font-mono">{ft.table}</span>
                </div>
              </div>

              <div className="text-xs">
                {!state.result ? (
                  <div className="space-y-2">
                    <input
                      type="file"
                      accept={ft.accept || '.csv'}
                      onChange={(e) => handleFileSelect(ft.key, e)}
                      className="hidden"
                      id={`target-file-${ft.key}`}
                    />
                    <label
                      htmlFor={`target-file-${ft.key}`}
                      className="flex items-center justify-center gap-1 px-3 py-2 bg-red-50 text-red-700 rounded hover:bg-red-100 cursor-pointer border border-red-200"
                    >
                      <Upload className="h-3.5 w-3.5" />
                      {state.file ? state.file.name.substring(0, 25) + (state.file.name.length > 25 ? '...' : '') : (ft.selectLabel || 'Select CSV')}
                    </label>
                    {state.file && (
                      <button
                        onClick={() => uploadFile(ft)}
                        disabled={state.uploading}
                        className="w-full flex items-center justify-center gap-1 px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                      >
                        {state.uploading
                          ? <><RefreshCw className="h-3.5 w-3.5 animate-spin" /> {state.progress || 0}%</>
                          : <><Play className="h-3.5 w-3.5" /> Upload</>
                        }
                      </button>
                    )}
                    {state.error && (
                      <div className="text-red-500 truncate text-[11px]">{state.error}</div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-1">
                    <div className="text-green-600 font-medium">
                      <CheckCircle className="h-3.5 w-3.5 inline mr-1" />
                      {state.result.rowCount?.toLocaleString()} rows uploaded
                    </div>
                    {state.result.metadata && (
                      <div className="text-[10px] text-gray-400 space-y-0.5">
                        {state.result.metadata['Exported at'] && <div>Exported: {state.result.metadata['Exported at']}</div>}
                        {state.result.metadata['Number of rows'] && <div>Source rows: {state.result.metadata['Number of rows']}</div>}
                        {state.result.metadata['Card Name'] && <div>{state.result.metadata['Card Name']}</div>}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>}
    </div>
  );
}
